import React, {
	useContext,
	useEffect,
	useMemo,
	useState,
	useCallback,
	memo,
} from 'react';
import './map.css';
import {
	MapContainer,
	Polyline,
	TileLayer,
	useMap,
	useMapEvents,
	Marker as LeafletMarker,
	Popup,
} from 'react-leaflet';
import {
	GroupedMarkers,
	LatLng,
	MapMarker,
	MapProps,
	MapView,
	MarkerIconProps,
	PinVariant,
	ReverseGeoMarker,
	SelectedLocationWithName,
	VariantMap,
} from './Map.interface';
import { Pickup } from '../../assets/Pickup';
import { Delivery } from '../../assets/Delivery';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { OrderStatusesEnum } from '../../services/orders.services';
import { CompletedPin } from '../../assets/CompletedPin';
import { TruckPinClient } from '../../assets/TruckPinClient';
import { DeliveryClient } from '../../assets/DeliveryClient';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';
import { MapPin } from '../../assets/MapPin';
import { PlusIcon } from '../../assets/PlusIcon';
import { MinusIcon } from '../../assets/MinusIcon';
import { AuthContext } from '../../context/context';
import { ChisinauCoords, WarsawCoords, zoomLevels } from './constants';
import ReverseGeoPin from './components/ReverseGeoPin';
import { GeoService } from '../../services/geo.services';
import FleetBaseIcon from '../../assets/FleetBaseIcon';
import MarkerContent from './components/MarkerContent';
import NewTruckIcon from '../../assets/NewTruckIcon';
import { MapLegendIcon } from '../../assets/MapLegendIcon';
import { T } from '@tolgee/react';
import { MapPinCircle } from '../../assets/MapPinCircle';
import SmallPickup from '../../assets/SmallPickup';
import SmallDelivery from '../../assets/SmallDelivery';
import RadiusCircle from './components/RadiusCircle';
import L, { LeafletEvent } from 'leaflet';
import ClusterMarker from './components/ClusterMarker';
import MarkerClusterWrapper from './components/MarkerClusterWrapper';
import ReactDOMServer from 'react-dom/server';
import BluePin from '../../assets/BluePin';
import TickIcon from '../../assets/TickIcon';

const MapClickHandler: React.FC<{ onMapClick: (latlng: LatLng) => void }> = ({
	onMapClick,
}) => {
	useMapEvents({
		click(e) {
			const target = e.originalEvent.target as HTMLElement;
			const isPopupClick = !!target.closest('.leaflet-popup');
			const isControlClick = !!target.closest('.map-ignore-click');

			if (!isPopupClick && !isControlClick) {
				onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
			}
		},
	});
	return null;
};

const ZoomTracker: React.FC<{ onZoomChange: (zoom: number) => void }> = ({
	onZoomChange,
}) => {
	const map = useMapEvents({
		zoomend: () => {
			onZoomChange(map.getZoom());
		},
	});

	useEffect(() => {
		onZoomChange(map.getZoom());
	}, []);

	return null;
};

const FitCustomerOrdersBounds: React.FC<{
	markers: MapMarker[];
	enabled: boolean;
}> = ({ markers, enabled }) => {
	const map = useMap();

	useEffect(() => {
		if (!enabled) return;
		if (!markers?.length) return;

		const points = markers
			.filter((m) => !Number.isNaN(m.latitude) && !Number.isNaN(m.longitude))
			.map((m) => L.latLng(m.latitude, m.longitude));

		if (points.length < 2) return;

		const bounds = L.latLngBounds(points);
		map.fitBounds(bounds, { padding: [40, 40] });
	}, [enabled, markers, map]);

	return null;
};

const ChangeMapView = memo(
	({
		coords,
		zoom,
		hasOffset,
	}: Partial<MapView> & {
		zoom?: number;
		hasOffset?: boolean;
	}) => {
		const map = useMap();
		const currentZoom = zoom || map.getZoom();

		const shouldApplyOffset = !!(hasOffset && coords && currentZoom < 15);
		const offset = shouldApplyOffset ? 1 / currentZoom : 0;

		if (!coords) return null;

		map.flyTo([coords.lat + offset, coords.lng], currentZoom, {
			animate: true,
			duration: 0.3,
		});

		return null;
	},
	(prev, next) => {
		return (
			prev.coords?.lat === next.coords?.lat &&
			prev.coords?.lng === next.coords?.lng &&
			prev.zoom === next.zoom &&
			prev.hasOffset === next.hasOffset
		);
	},
);

const FullscreenHandler: React.FC<{ isMapFullScreen: boolean }> = ({
	isMapFullScreen,
}) => {
	const map = useMap();

	useEffect(() => {
		const timer = setTimeout(() => {
			map.invalidateSize();
		}, 100);

		return () => clearTimeout(timer);
	}, [isMapFullScreen, map]);

	return null;
};

const Map: React.FC<MapProps> = ({
	polygons,
	markers,
	markerHovered,
	orderStatus,
	currentMarker,
	target = 'transporter',
	setBaseRadius,
	isMapFullScreen,
	// setIsMapFullScreen,
	selectedLocation,
	onConfirmLocation,
	onReversePin,
	mapContainerKey,
	fitMarkersToBounds,
}) => {
	const { user } = useContext(AuthContext);
	const [center, setCenter] = useState<LatLng>(
		user?.profile?.countryCode === 'MD' ? ChisinauCoords : WarsawCoords,
	);
	const [initialZoom, setInitialZoom] = useState<number>(10);
	const [currentRadius, setCurrentRadius] = useState<number | undefined>(
		undefined,
	);
	const [leftEdge, setLeftEdge] = useState<LatLng | undefined>(undefined);
	const [rightEdge, setRightEdge] = useState<LatLng | undefined>(undefined);
	const [currentZoomLevel, setCurrentZoomLevel] = useState<number>(10);
	const [userMarker, setUserMarker] = useState<ReverseGeoMarker | null>(null);
	const [shouldUpdateView, setShouldUpdateView] = useState<boolean>(true);
	const [viewMapLegend, setViewMapLegend] = useState<boolean>(false);
	const productLocation = useMemo(
		() => markers?.find((r) => r.variant === PinVariant.product),
		[markers],
	);
	const baseLocation = useMemo(
		() => markers?.find((r) => r.variant === PinVariant.fleet_base),
		[markers],
	);
	const [currentMarkerPosition, setCurrentMarkerPosition] = useState<
		LatLng | undefined
	>(
		baseLocation?.latitude && baseLocation?.longitude
			? { lat: baseLocation.latitude, lng: baseLocation.longitude }
			: undefined,
	);
	const [currentView, setCurrentView] = useState<MapView & { zoom?: number }>({
		coords: user?.profile?.countryCode === 'MD' ? ChisinauCoords : WarsawCoords,
		zoom: 10,
	});
	const [exactClickPosition, setExactClickPosition] = useState<LatLng | null>(
		null,
	);
	const [bothPinsVisible, setBothPinsVisible] = useState<boolean>(false);

	const isCustomerTarget =
		target === OrderTarget.customer || target === 'customer';
	const isProducerTarget = target === 'producer';
	const shouldZoomToLocation = isCustomerTarget || isProducerTarget;

	const isRecentOrderSelection =
		isCustomerTarget && !!(selectedLocation as any)?.fromRecentOrder;

	useEffect(() => {
		if (isRecentOrderSelection) {
			setExactClickPosition(null);
			setBothPinsVisible(false);
		}
	}, [isRecentOrderSelection]);

	const blueUserIcon = useMemo(
		() =>
			L.divIcon({
				html: ReactDOMServer.renderToString(
					<div className="flex flex-col items-center">
						<div className="relative">
							<BluePin />
						</div>
					</div>,
				),
				className: 'custom-user-marker',
				iconSize: [42, 42],
				iconAnchor: [21, 42],
			}),
		[],
	);

	const MERGE_DISTANCE_KM = 0.025;

	const haversineKm = (a: LatLng, b: LatLng): number => {
		const toRad = (x: number) => (x * Math.PI) / 180;
		const R = 6371;
		const dLat = toRad(b.lat - a.lat);
		const dLng = toRad(b.lng - a.lng);
		const lat1 = toRad(a.lat);
		const lat2 = toRad(b.lat);
		const h =
			Math.sin(dLat / 2) ** 2 +
			Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
		return 2 * R * Math.asin(Math.sqrt(h));
	};

	const reverseGeocode = useCallback(
		async (lat: number, lng: number): Promise<string> => {
			try {
				const response = await GeoService.reverseGeocode({
					lat: lat.toString(),
					lng: lng.toString(),
					countryCode: user?.profile?.countryCode,
				});

				const first = response.data?.items?.[0];
				if (first?.address?.label) return first.address.label;

				const parts = [
					first?.address?.street,
					first?.address?.city,
					first?.address?.state,
					first?.address?.postalCode,
					first?.address?.countryName,
				].filter(Boolean);

				return parts.length
					? parts.join(', ')
					: `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
			} catch {
				return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
			}
		},
		[user?.profile?.countryCode],
	);

	const handleMapClick = async (latlng: LatLng) => {
		setShouldUpdateView(false);

		try {
			const { data } = await GeoService.reverseGeocode({
				lat: latlng.lat.toString(),
				lng: latlng.lng.toString(),
				countryCode: user?.profile?.countryCode,
			});
			const first = data.items?.[0];

			if (!first) {
				setExactClickPosition(null);
				setBothPinsVisible(false);
				return;
			}

			const { position, address } = first;
			const reversePos: LatLng = { lat: position.lat, lng: position.lng };

			const distance = haversineKm(latlng, reversePos);
			const bothPins = distance > MERGE_DISTANCE_KM;
			setBothPinsVisible(bothPins);

			if (bothPins) {
				setExactClickPosition(latlng);
			} else {
				setExactClickPosition(null);
			}

			const newGeo: any = {
				locationId: first.id,
				latitude: position.lat,
				longitude: position.lng,
				address,
				countryCode: address.countryCode,
				exactClickPosition: bothPins ? latlng : null,
			};

			setCurrentMarkerPosition(reversePos);

			const centerTo = bothPins ? latlng : reversePos;

			const zoomLevel = shouldZoomToLocation ? 18 : currentView.zoom;

			setCurrentView({
				...currentView!,
				coords: centerTo,
				zoom: zoomLevel,
			});

			setUserMarker({
				id: `user-marker-${Date.now()}`,
				position: reversePos,
				locationName: address.label,
			});

			if (target === 'transporter' && baseLocation?.radius?.isAdjustable) {
				const radiusToUse = currentRadius ?? baseLocation.radius.value;
				setCurrentRadius(radiusToUse);
				setBaseRadius?.(radiusToUse!);
			}

			onReversePin?.(newGeo);
		} catch (e) {
			console.error(e);
			setExactClickPosition(null);
			setBothPinsVisible(false);
		}
	};

	const handleMarkerDrag = (e: LeafletEvent) => {
		const position = e.target.getLatLng();
		if (userMarker?.locationName) {
			setUserMarker({
				...userMarker!,
				locationName: '',
			});
		}
		setCurrentMarkerPosition(position);
	};

	const handleMarkerDragEnd = async (newPosition: LatLng) => {
		setShouldUpdateView(false);
		try {
			const { data } = await GeoService.reverseGeocode({
				lat: newPosition.lat.toString(),
				lng: newPosition.lng.toString(),
				countryCode: user?.profile?.countryCode,
			});
			const first = data.items?.[0];
			if (!first) return;

			const { position, address } = first;
			const updatedGeo: any = {
				locationId: first.id,
				latitude: position.lat,
				longitude: position.lng,
				address,
				countryCode: address.countryCode,
				exactClickPosition: exactClickPosition ?? null,
			};

			setUserMarker({
				id: userMarker!.id,
				position: { lat: position.lat, lng: position.lng },
				locationName: address.label,
			});

			let zoomLevel = currentView.zoom;
			if (shouldZoomToLocation) {
				zoomLevel = 18;
			} else if (baseLocation?.radius?.value) {
				zoomLevel = updateZoomLevel(baseLocation.radius.value);
			}

			const centerTo: LatLng = exactClickPosition ?? {
				lat: position.lat,
				lng: position.lng,
			};

			setCurrentView({
				...currentView!,
				coords: centerTo,
				zoom: zoomLevel,
			});

			setCurrentMarkerPosition({ lat: position.lat, lng: position.lng });
			onReversePin?.(updatedGeo);
		} catch (e) {
			console.error(e);
		}
	};

	const removeUserMarker = (e?: React.MouseEvent) => {
		if (e) {
			e.stopPropagation();
			const popup = (e.target as any).closest('.leaflet-popup');
			if (popup) popup.style.display = 'none';
		}
		setUserMarker(null);
		setShouldUpdateView(false);
		setExactClickPosition(null);
		setBothPinsVisible(false);
	};

	const handleConfirm = React.useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			if (!onConfirmLocation || !selectedLocation) return;

			const coordsToUse = exactClickPosition ?? userMarker?.position;
			if (!coordsToUse) return;

			const locationForOrder = {
				...selectedLocation,
				latitude: coordsToUse.lat,
				longitude: coordsToUse.lng,
			} as any;

			onConfirmLocation(locationForOrder);
			removeUserMarker(e);
		},
		[onConfirmLocation, selectedLocation, exactClickPosition, userMarker],
	);

	useEffect(() => {
		let newCenter: LatLng | null = null;

		if (
			fitMarkersToBounds &&
			isCustomerTarget &&
			markers?.length &&
			markers.length > 1 &&
			!userMarker
		) {
			return;
		}

		if (baseLocation && !userMarker) {
			newCenter = {
				lat: baseLocation.latitude,
				lng: baseLocation.longitude,
			};
			setCurrentRadius(baseLocation?.radius?.value);
		} else if (markers?.length && !userMarker) {
			const variants = [
				PinVariant.truck,
				PinVariant.pickup,
				PinVariant.product,
				PinVariant.delivery,
				PinVariant.product_store,
			];
			let start = variants
				.map((v) => markers.find((r) => r.variant === v))
				.find((result) => result);

			if (start) {
				newCenter = {
					lat: start.latitude,
					lng: start.longitude,
				};
			}
		} else if (polygons?.length) {
			const start = polygons[0].coordinates[0];
			if (start) {
				newCenter = start;
			}
		}

		if (newCenter) {
			setCurrentView({
				...currentView,
				coords: { lat: newCenter.lat, lng: newCenter.lng },
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		polygons,
		markers,
		currentMarker,
		baseLocation,
		userMarker,
		fitMarkersToBounds,
	]);

	useEffect(() => {
		if (!selectedLocation) {
			setUserMarker(null);
			setExactClickPosition(null);
			setBothPinsVisible(false);

			if (!isCustomerTarget) {
				setCurrentView({
					...currentView,
					coords:
						user?.profile?.countryCode === 'MD' ? ChisinauCoords : WarsawCoords,
					zoom: target === 'customer' ? 10 : undefined,
				});
			}

			return;
		}

		const hasLocationName = (selectedLocation as SelectedLocationWithName)
			?.locationName;

		if (hasLocationName) {
			const zoomLevel = shouldZoomToLocation ? 18 : currentView.zoom;

			const preferCoords =
				bothPinsVisible && exactClickPosition
					? exactClickPosition
					: {
							lat: selectedLocation.latitude!,
							lng: selectedLocation.longitude!,
					  };

			const finalZoom = bothPinsVisible
				? currentView.zoom ?? zoomLevel
				: zoomLevel;

			setCurrentView({
				...currentView,
				coords: preferCoords,
				zoom: finalZoom,
			});

			setCurrentMarkerPosition({
				lat: selectedLocation.latitude!,
				lng: selectedLocation.longitude!,
			});

			setUserMarker({
				id: `selected-${selectedLocation.locationId}`,
				position: {
					lat: selectedLocation.latitude!,
					lng: selectedLocation.longitude!,
				},
				locationName: hasLocationName,
			});
		} else {
			(async () => {
				setShouldUpdateView(false);
				const locationName = await reverseGeocode(
					selectedLocation.latitude!,
					selectedLocation.longitude!,
				);

				const zoomLevel = shouldZoomToLocation ? 18 : currentView.zoom;

				const preferCoords =
					bothPinsVisible && exactClickPosition
						? exactClickPosition
						: {
								lat: selectedLocation.latitude!,
								lng: selectedLocation.longitude!,
						  };

				const finalZoom = bothPinsVisible
					? currentView.zoom ?? zoomLevel
					: zoomLevel;

				setCurrentView({
					...currentView,
					coords: preferCoords,
					zoom: finalZoom,
				});

				setCurrentMarkerPosition({
					lat: selectedLocation.latitude!,
					lng: selectedLocation.longitude!,
				});

				setUserMarker({
					id: `selected-${selectedLocation.locationId}`,
					position: {
						lat: selectedLocation.latitude!,
						lng: selectedLocation.longitude!,
					},
					locationName,
				});
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedLocation, bothPinsVisible, exactClickPosition]);

	const iconTransporter = (variant: PinVariant): JSX.Element => {
		if (
			orderStatus &&
			[
				OrderStatusesEnum.COMPLETED.toString(),
				OrderStatusesEnum.DELIVERED.toString(),
			].includes(orderStatus)
		) {
			return <CompletedPin />;
		}

		const variantMap: VariantMap = {
			[PinVariant.pickup]: <Pickup />,
			[PinVariant.delivery]: <Delivery />,
			[PinVariant.product]: <MapPin color="#FFB352" />,
			[PinVariant.fleet_base]: <FleetBaseIcon />,
			[PinVariant.product_store]: <MapPin color="#FFB352" />,
		};

		return variantMap[variant] || <NewTruckIcon />;
	};

	const iconCustomer = (variant: PinVariant): JSX.Element => {
		const customerIcons: { [key in PinVariant]?: JSX.Element } = {
			[PinVariant.pickup]: <MapPin color="#FFB352" fillColor="white" />,
			[PinVariant.delivery]: <DeliveryClient />,
		};

		return customerIcons[variant] || <TruckPinClient />;
	};

	const icon = (variant: PinVariant): JSX.Element => {
		return target === OrderTarget.customer
			? iconCustomer(variant)
			: iconTransporter(variant);
	};

	const updateZoomLevel = (radius: number) =>
		zoomLevels[radius] ?? (productLocation ? 14 : 10);

	useEffect(() => {
		if (typeof currentRadius === 'number') {
			setInitialZoom(updateZoomLevel(currentRadius));
			setCurrentView({ ...currentView!, zoom: updateZoomLevel(currentRadius) });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentRadius]);

	const handleRadiusChange = (action: 'decrease' | 'increase') => {
		const radiusValues = [25, 50, 75, 100, 200, 300, 400, 500];

		const currentIndex = currentRadius
			? radiusValues.indexOf(currentRadius)
			: -1;
		if (currentIndex !== -1) {
			if (action === 'decrease' && currentIndex > 0) {
				setCurrentRadius(radiusValues[currentIndex - 1]);
				if (setBaseRadius) {
					setBaseRadius(radiusValues[currentIndex - 1]);
				}
			} else if (
				action === 'increase' &&
				currentIndex < radiusValues.length - 1
			) {
				setCurrentRadius(radiusValues[currentIndex + 1]);
				if (setBaseRadius) {
					setBaseRadius(radiusValues[currentIndex + 1]);
				}
			}
		}
	};

	const getCircleEdgeCoords = (center: LatLng, radiusInMeters: number) => {
		const earthRadius = 6378137;
		const latInRad = (center.lat * Math.PI) / 180;
		const deltaLng =
			(radiusInMeters / (earthRadius * Math.cos(latInRad))) * (180 / Math.PI);

		const leftEdge = {
			lat: center.lat,
			lng: center.lng - deltaLng,
		};

		const rightEdge = {
			lat: center.lat,
			lng: center.lng + deltaLng,
		};

		return { leftEdge, rightEdge };
	};

	useEffect(() => {
		if (currentRadius && currentMarkerPosition) {
			const { leftEdge, rightEdge } = getCircleEdgeCoords(
				currentMarkerPosition!,
				currentRadius * 1000,
			);
			setLeftEdge(leftEdge);
			setRightEdge(rightEdge);
		}
	}, [currentRadius, currentMarkerPosition]);

	useEffect(() => {
		if (baseLocation?.radius?.value !== undefined) {
			setCurrentRadius(baseLocation.radius.value);
		}
	}, [baseLocation?.radius?.value]);

	useEffect(() => {
		if (baseLocation?.latitude && baseLocation?.longitude && !userMarker) {
			setCurrentMarkerPosition({
				lat: baseLocation.latitude,
				lng: baseLocation.longitude,
			});
		}
	}, [baseLocation, userMarker]);

	const MarkerIcon = ({
		fleetBase,
		orderProduct,
		productStore,
		markerHovered,
		variant,
	}: MarkerIconProps) => {
		const shouldHideMarker =
			!!userMarker &&
			(isCustomerTarget ||
				variant === PinVariant.fleet_base ||
				variant === PinVariant.product_store);

		return (
			<div
				className={`${
					fleetBase || productStore
						? 'w-[42px] h-[42px] flex justify-center items-center bg-white rounded-full'
						: ''
				} ${
					orderProduct && markerHovered === orderProduct.name
						? 'w-[32px] h-[32px] flex justify-center items-center bg-white rounded-full'
						: ''
				} ${shouldHideMarker ? 'opacity-0' : ''}`}
			>
				{icon(variant)}
			</div>
		);
	};

	const renderMarkerContent = (
		marker: MapMarker,
		shouldShowCluster = false,
		orderProductCount?: number,
	) => {
		const delivery: any = marker?.delivery ?? marker;
		const pickup: any = marker?.pickup ?? marker;
		const fleetBase = marker?.fleetBase || marker?.store;
		const orderProduct = marker?.orderProduct;
		const productStore = marker?.store;

		return (
			<div className="flex flex-col items-center -mt-5">
				<div className="relative">
					{shouldShowCluster ? (
						<div className="w-[44px] h-[44px] flex justify-center items-center bg-midnight-blue rounded-full">
							<SypacText variant="body-regular-medium">
								<p className="text-white">{orderProductCount}</p>
							</SypacText>
						</div>
					) : (
						<MarkerIcon
							fleetBase={fleetBase!}
							orderProduct={orderProduct!}
							productStore={productStore!}
							markerHovered={markerHovered!}
							variant={marker.variant as PinVariant}
						/>
					)}
				</div>

				{!shouldShowCluster && (
					<MarkerContent
						marker={marker}
						delivery={delivery}
						pickup={pickup}
						fleetBase={fleetBase}
						orderProduct={orderProduct}
						productStore={productStore}
						markerHovered={markerHovered}
						target={target}
					/>
				)}
			</div>
		);
	};

	const getTileKey = (): string | undefined => {
		return process.env.REACT_APP_TILE_LAYER_KEY;
	};

	return (
		<div className="w-full h-full relative">
			{currentMarker && currentMarker.variant ? (
				<div className="absolute z-[500] top-12 flex flex-row w-full justify-center">
					<div className="rounded-lg bg-cornflower-blue px-4 py-2 flex flex-col">
						<SypacText variant="body-regular-large">
							<p className="text-white">
								Current location: 7 Rue Fernande Folral str., Charleroi, BE
							</p>
						</SypacText>
						<SypacText variant="overline-regular-large" className="mt-3">
							<p className="text-white">
								Latitude: 50.4, Longitude: 4.4333350° 24′ 0″ North, 4° 25′ 60″
								East
							</p>
						</SypacText>
					</div>
				</div>
			) : null}

			<MapContainer
				center={[currentView?.coords.lat, currentView?.coords.lng]}
				zoom={
					baseLocation?.radius?.isAdjustable ? initialZoom : initialZoom - 2
				}
				scrollWheelZoom={true}
				zoomControl={false}
				className="w-full h-full rounded-10 z-0"
				key={mapContainerKey || `${center.lat}${center.lng}${initialZoom}`}
			>
				<TileLayer
					url={`https://api.maptiler.com/maps/basic-v2/{z}/{x}/{y}.png?key=${getTileKey()}`}
					attribution={`<div>Logo</div>`}
					tileSize={512}
					zoomOffset={-1}
					className="grayscale-tiles"
				/>

				{(onReversePin ||
					(target === 'transporter' && baseLocation?.radius?.isAdjustable)) && (
					<MapClickHandler onMapClick={handleMapClick} />
				)}
				<FullscreenHandler isMapFullScreen={isMapFullScreen || false} />
				<ZoomTracker onZoomChange={setCurrentZoomLevel} />

				<FitCustomerOrdersBounds
					markers={markers || []}
					enabled={
						isCustomerTarget &&
						!!fitMarkersToBounds &&
						!!markers?.length &&
						markers.length > 1 &&
						!userMarker &&
						!selectedLocation
					}
				/>

				{exactClickPosition && !isRecentOrderSelection && (
					<LeafletMarker
						position={[exactClickPosition.lat, exactClickPosition.lng]}
						icon={blueUserIcon}
						eventHandlers={{
							add: (e) => {
								setTimeout(() => {
									try {
										e.target.openPopup();
									} catch {}
								}, 300);
							},
						}}
					>
						<Popup
							offset={[0, -44]}
							autoClose={false}
							closeOnClick={false}
							autoPan={false}
							keepInView={false}
						>
							<div className="flex flex-col gap-3 text-left">
								<SypacText variant="body-regular-small">
									<p className="text-[20px] leading-[32px] text-gray-80 !m-0">
										<T keyName="map.offRoadLocation">
											Off-road location detected. Ensure truck access.
										</T>
									</p>
								</SypacText>
								<SypacText variant="overline-regular-small">
									<p className="text-[14px] leading-[20px] text-gray-40 !m-0">
										Lat: {exactClickPosition.lat.toFixed(6)}, Lng:{' '}
										{exactClickPosition.lng.toFixed(6)}
									</p>
								</SypacText>

								{userMarker?.position && target !== 'transporter' ? (
									<SypacButton
										variant="secondary"
										size="small"
										className="w-full mt-2"
									>
										<button
											type="button"
											onClick={handleConfirm}
											className="w-full h-[32px] flex gap-3 items-center justify-center bg-primary-violet border-0 rounded-lg p-0 cursor-pointer transition hover:bg-primary-violet/80"
										>
											<TickIcon />
											<SypacText variant="body-regular-medium">
												<p className="text-white">
													<T keyName="map.confirm">Confirm</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
								) : null}
							</div>
						</Popup>
					</LeafletMarker>
				)}

				{userMarker && (
					<ReverseGeoPin
						marker={userMarker}
						onDragEnd={handleMarkerDragEnd}
						handleMarkerDrag={handleMarkerDrag}
						currentMarkerPosition={currentMarkerPosition}
						onConfirm={handleConfirm}
						showConfirm={!exactClickPosition}
						target={target}
					/>
				)}
				{exactClickPosition &&
				userMarker?.position &&
				!isRecentOrderSelection ? (
					<Polyline
						positions={[
							[exactClickPosition.lat, exactClickPosition.lng],
							[userMarker.position.lat, userMarker.position.lng],
						]}
						pathOptions={{
							color: '#1D4ED8',
							weight: 3,
							dashArray: '6 8',
							opacity: 0.9,
						}}
					/>
				) : null}

				{currentMarker && currentMarker.variant ? (
					<LeafletMarker
						position={[currentMarker.latitude, currentMarker.longitude]}
						icon={L.divIcon({
							html: ReactDOMServer.renderToString(
								<div className="flex flex-col items-center -mt-5">
									{icon(currentMarker.variant as PinVariant)}
								</div>,
							),
							iconSize: [100, 32],
							iconAnchor: [50, 16],
							className: 'custom-div-icon',
						})}
					>
						{currentMarker.variant === PinVariant.truck &&
							currentMarker?.truck && (
								<Popup>
									<div>
										<div
											className={`py-[1px] px-4 rounded-3xl flex flex-row ${
												currentMarker?.truck?.status === 'not_available'
													? 'bg-texas-rose'
													: 'bg-mountain-meadow'
											}`}
										>
											<SypacText variant="body-regular-small">
												<p className="font-medium text-white max-w-[60px] truncate">
													{currentMarker.truck?.truckModel}
												</p>
											</SypacText>
											<SypacText variant="body-regular-small">
												<p className="font-medium text-white">
													#{currentMarker.truck?.id}
												</p>
											</SypacText>
										</div>
										<div className="p-3 flex flex-row bg-white rounded-10 mt-2 shadow-map-card">
											<div
												style={{
													backgroundImage: `url(${
														currentMarker.truck?.truckPhoto
															? currentMarker.truck?.truckPhoto
															: '/truck.jpg'
													})`,
												}}
												className="flex w-[58px] bg-center bg-cover bg-no-repeat h-[58px] rounded-md"
												role="img"
											/>
											<div className="ml-3 flex flex-col justify-center">
												<div className="flex gap-1.5 items-center">
													<SypacText variant="overline-regular-large">
														<p className="text-gray-90">
															{currentMarker.truck?.truckModel}
														</p>
													</SypacText>
													<SypacText variant="overline-regular-large">
														<p className="text-gray-90">
															#{currentMarker.truck?.id}
														</p>
													</SypacText>
												</div>
												<div className="bg-gray-10-opacity-50 rounded-md px-4 py-1 mt-2">
													{currentMarker.truck?.truckNumber}
												</div>
											</div>
										</div>
									</div>
								</Popup>
							)}
					</LeafletMarker>
				) : null}

				{markers?.length
					? ((): any => {
							if (isCustomerTarget && userMarker) {
								return null;
							}

							if (target === OrderTarget.customer) {
								// @ts-ignore
								const groupedMarkers: GroupedMarkers = markers.reduce(
									(acc: GroupedMarkers, marker: MapMarker) => {
										const key = `${marker.latitude},${marker.longitude}`;
										if (!acc[key]) {
											acc[key] = [];
										}
										acc[key].push(marker);
										return acc;
									},
									{},
								);

								return Object.entries(groupedMarkers).map(
									([coords, markerGroup], index) => {
										const [latitude, longitude] = coords.split(',').map(Number);
										const firstMarker: MapMarker = markerGroup[0];
										const orderProductCount = markerGroup.filter(
											(m: MapMarker) => m.orderProduct,
										).length;
										const shouldShowCluster = orderProductCount >= 2;

										return (
											<LeafletMarker
												key={index}
												position={[latitude, longitude]}
												icon={L.divIcon({
													html: ReactDOMServer.renderToString(
														renderMarkerContent(
															firstMarker,
															shouldShowCluster,
															orderProductCount,
														),
													),
													iconSize: [0, 0],
													iconAnchor: [0, 0],
													className: 'custom-div-icon',
												})}
											/>
										);
									},
								);
							}

							const truckMarkers = markers.filter(
								(m) => m.variant === PinVariant.truck,
							);
							const pickupMarkers = markers.filter(
								(m) => m.variant === PinVariant.pickup,
							);
							const deliveryMarkers = markers.filter(
								(m) => m.variant === PinVariant.delivery,
							);
							const otherMarkers = markers.filter(
								(m) =>
									m.variant !== PinVariant.truck &&
									m.variant !== PinVariant.pickup &&
									m.variant !== PinVariant.delivery,
							);

							return (
								<>
									{truckMarkers.length > 1 ? (
										<MarkerClusterWrapper>
											{truckMarkers.map((marker) => (
												<ClusterMarker
													variant={marker.variant as PinVariant.truck}
													key={
														marker.truck?.id ||
														`truck-${marker.latitude}-${marker.longitude}`
													}
													position={[marker.latitude, marker.longitude]}
													truck={marker.truck!}
												/>
											))}
										</MarkerClusterWrapper>
									) : (
										truckMarkers.map((marker, index) => (
											<LeafletMarker
												key={`truck-${marker.truck?.id ?? index}`}
												position={[marker.latitude, marker.longitude]}
												icon={L.divIcon({
													html: ReactDOMServer.renderToString(
														renderMarkerContent(marker),
													),
													iconSize: [0, 0],
													iconAnchor: [0, 0],
													className: 'custom-div-icon',
												})}
											/>
										))
									)}

									{pickupMarkers.map((marker, index) => (
										<LeafletMarker
											key={`pickup-${index}`}
											position={[marker.latitude, marker.longitude]}
											icon={L.divIcon({
												html: ReactDOMServer.renderToString(
													renderMarkerContent(marker),
												),
												iconSize: [0, 0],
												iconAnchor: [0, 0],
												className: 'custom-div-icon',
											})}
										/>
									))}

									{deliveryMarkers.map((marker, index) => (
										<LeafletMarker
											key={`delivery-${index}`}
											position={[marker.latitude, marker.longitude]}
											icon={L.divIcon({
												html: ReactDOMServer.renderToString(
													renderMarkerContent(marker),
												),
												iconSize: [0, 0],
												iconAnchor: [0, 0],
												className: 'custom-div-icon',
											})}
										/>
									))}

									{otherMarkers.map((marker, index) => {
										if (
											(marker.variant === PinVariant.fleet_base ||
												marker.variant === PinVariant.product_store) &&
											userMarker
										) {
											return (
												<LeafletMarker
													key={`other-${index}`}
													position={[marker.latitude, marker.longitude]}
													icon={L.divIcon({
														html: ReactDOMServer.renderToString(
															<div style={{ opacity: 0 }}>
																{renderMarkerContent(marker)}
															</div>,
														),
														iconSize: [0, 0],
														iconAnchor: [0, 0],
														className: 'custom-div-icon',
													})}
												/>
											);
										}

										return (
											<LeafletMarker
												key={`other-${index}`}
												position={[marker.latitude, marker.longitude]}
												icon={L.divIcon({
													html: ReactDOMServer.renderToString(
														renderMarkerContent(marker),
													),
													iconSize: [0, 0],
													iconAnchor: [0, 0],
													className: 'custom-div-icon',
												})}
											/>
										);
									})}
								</>
							);
					  })()
					: null}

				{polygons?.length ? (
					<>
						{(() => {
							const routes = polygons.filter((p) => !(p as any).connector);

							const order = (p: any) => (p.id === 1 ? 2 : p.id === 0 ? 1 : 0);

							return [...routes]
								.sort((a: any, b: any) => order(a) - order(b))
								.map((polygon: any, idx: number) => {
									const isMain = polygon.id === 1;
									const isTruckToPickup = polygon.id === 0;

									const pathStyle = isMain
										? { color: '#0C987F', weight: 6 }
										: isTruckToPickup
										? { color: '#EB9F3E', weight: 4 }
										: { color: '#A2B8F4', weight: 4 };

									return (
										<Polyline
											key={polygon.id ?? idx}
											positions={polygon.coordinates}
											pathOptions={pathStyle}
										/>
									);
								});
						})()}

						{polygons
							.filter((p) => (p as any).connector)
							.map((poly: any, cIdx: number) => (
								<Polyline
									key={`connector-${poly.id ?? cIdx}`}
									positions={poly.coordinates}
									pathOptions={{
										color: '#1D4ED8',
										weight: 3,
										dashArray: '6 8',
										opacity: 0.9,
									}}
								/>
							))}
					</>
				) : null}

				{center ? (
					<ChangeMapView {...currentView!} hasOffset={!!userMarker} />
				) : null}

				{productLocation ? <RadiusCircle location={productLocation} /> : null}

				{isRecentOrderSelection && userMarker?.position ? (
					<RadiusCircle
						location={{
							latitude: userMarker.position.lat,
							longitude: userMarker.position.lng,
						}}
						radius={500 * 1000}
					/>
				) : null}

				{baseLocation?.radius?.value ? (
					<>
						{currentMarkerPosition ? (
							<RadiusCircle
								location={{
									latitude: currentMarkerPosition.lat,
									longitude: currentMarkerPosition.lng,
								}}
								radius={currentRadius ? currentRadius * 1000 : 1000}
							/>
						) : null}

						{baseLocation?.radius?.isAdjustable && leftEdge && rightEdge ? (
							<>
								<LeafletMarker
									position={[leftEdge.lat!, leftEdge.lng!]}
									icon={L.divIcon({
										html: ReactDOMServer.renderToString(
											<button
												type="button"
												className={`${
													currentZoomLevel < 10
														? 'w-[32px] h-[32px]'
														: currentZoomLevel < 12
														? 'w-[42px] h-[42px]'
														: 'w-[52px] h-[52px]'
												} flex justify-center items-center bg-texas-rose border-2 border-solid border-white rounded-full cursor-pointer map-ignore-click`}
											>
												<span
													className={`${
														currentZoomLevel < 10
															? 'scale-[1.0]'
															: currentZoomLevel < 12
															? 'scale-[1.3]'
															: 'scale-[1.6]'
													} m-0 p-0 mt-[2px]`}
												>
													<MinusIcon color="white" />
												</span>
											</button>,
										),
										iconSize: [
											currentZoomLevel < 10
												? 32
												: currentZoomLevel < 12
												? 42
												: 52,
											currentZoomLevel < 10
												? 32
												: currentZoomLevel < 12
												? 42
												: 52,
										],
										iconAnchor: [
											currentZoomLevel < 10
												? 16
												: currentZoomLevel < 12
												? 21
												: 26,
											currentZoomLevel < 10
												? 16
												: currentZoomLevel < 12
												? 21
												: 26,
										],
										className: 'custom-div-icon',
									})}
									eventHandlers={{
										click: () => handleRadiusChange('decrease'),
									}}
								/>

								<LeafletMarker
									position={[rightEdge.lat!, rightEdge.lng!]}
									icon={L.divIcon({
										html: ReactDOMServer.renderToString(
											<button
												type="button"
												className={`${
													currentZoomLevel < 10
														? 'w-[32px] h-[32px]'
														: currentZoomLevel < 12
														? 'w-[42px] h-[42px]'
														: 'w-[52px] h-[52px]'
												} flex justify-center items-center bg-mountain-meadow border-2 border-solid border-white rounded-full cursor-pointer map-ignore-click`}
											>
												<span
													className={`${
														currentZoomLevel < 10
															? 'scale-[1.0]'
															: currentZoomLevel < 12
															? 'scale-[1.3]'
															: 'scale-[1.6]'
													} m-0 p-0 mt-[2px]`}
												>
													<PlusIcon color="white" />
												</span>
											</button>,
										),
										iconSize: [
											currentZoomLevel < 10
												? 32
												: currentZoomLevel < 12
												? 42
												: 52,
											currentZoomLevel < 10
												? 32
												: currentZoomLevel < 12
												? 42
												: 52,
										],
										iconAnchor: [
											currentZoomLevel < 10
												? 16
												: currentZoomLevel < 12
												? 21
												: 26,
											currentZoomLevel < 10
												? 16
												: currentZoomLevel < 12
												? 21
												: 26,
										],
										className: 'custom-div-icon',
									})}
									eventHandlers={{
										click: () => handleRadiusChange('increase'),
									}}
								/>
							</>
						) : null}
					</>
				) : null}

				{/*{target === 'customer' &&*/}
				{/*	(isMapFullScreen ? (*/}
				{/*		<SypacButton*/}
				{/*			variant="secondary"*/}
				{/*			size="small"*/}
				{/*			className="absolute top-[10px] right-[10px] w-fit z-[999] map-ignore-click"*/}
				{/*		>*/}
				{/*			<button*/}
				{/*				type="button"*/}
				{/*				className="w-[32px] h-[32px] flex items-center justify-center p-0 border border-solid border-[#E2E5E9] rounded-[6px] bg-white"*/}
				{/*				onClick={() => setIsMapFullScreen && setIsMapFullScreen(false)}*/}
				{/*			>*/}
				{/*				<Close width="14" height="14" color="#8B8B8B" />*/}
				{/*			</button>*/}
				{/*		</SypacButton>*/}
				{/*	) : (*/}
				{/*		<SypacButton*/}
				{/*			variant="secondary"*/}
				{/*			size="small"*/}
				{/*			className="absolute top-[10px] right-[10px] w-fit z-[999] map-ignore-click"*/}
				{/*		>*/}
				{/*			<button*/}
				{/*				type="button"*/}
				{/*				className="w-[32px] h-[32px] flex items-center justify-center p-0 border border-solid border-[#E2E5E9] rounded-[6px] bg-white"*/}
				{/*				onClick={() => setIsMapFullScreen && setIsMapFullScreen(true)}*/}
				{/*			>*/}
				{/*				<MaximizeMap />*/}
				{/*			</button>*/}
				{/*		</SypacButton>*/}
				{/*	))}*/}

				{target === 'transporter' &&
					mapContainerKey !== 'fleet-map-container' &&
					(!viewMapLegend ? (
						<SypacButton
							variant="secondary"
							size="small"
							className="absolute bottom-[10px] right-[10px] w-fit z-[999] map-ignore-click"
						>
							<button
								type="button"
								className="w-[24px] h-[24px] flex items-center justify-center p-0 border border-solid border-[#E2E5E9] rounded-[6px] bg-white"
								onClick={() => setViewMapLegend(true)}
							>
								<MapLegendIcon />
							</button>
						</SypacButton>
					) : (
						<div
							onClick={() => setViewMapLegend(false)}
							className="absolute bottom-[10px] right-[10px] z-[9999] map-ignore-click min-w-[226px] h-fit flex flex-col gap-[6px] p-[6px] bg-white border border-solid border-gray-10 rounded-[4px] box-border"
						>
							<div className="h-[16px] grid grid-cols-[62px,auto] gap-3 items-center">
								<div className="w-full h-[7px] bg-[#0C987F] rounded-full" />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.pathToUnloading">
											Path to unloading location
										</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[62px,auto] gap-3 items-center">
								<div className="w-full h-[4px] bg-[#EB9F3E] rounded-full" />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.pathToLoading">Path to loading location</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[62px,auto] gap-3 items-center">
								<div className="w-full h-[4px] bg-[#A2B8F4] rounded-full" />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.alternativeRoute">Alternative route</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[62px,auto] gap-3 items-center">
								<div className="relative">
									<div
										className="absolute -top-[2px] inset-0 bg-gray-80 rounded-full h-[4px]"
										style={{
											backgroundImage:
												'repeating-linear-gradient(90deg, transparent, transparent 10px, #fff 4px, #fff 18px)',
										}}
									/>
								</div>
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.offRoadDeliverySegment">
											Off-road delivery segment
										</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[16px,auto] gap-3 items-center">
								<MapPinCircle />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.currentLocationTruck">
											Current location of the truck
										</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[16px,auto] gap-3 items-center">
								<SmallPickup />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.loadingPlace">Loading place</T>
									</p>
								</SypacText>
							</div>
							<div className="h-[16px] grid grid-cols-[16px,auto] gap-3 items-center">
								<SmallDelivery />
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[16px] text-gray-80">
										<T keyName="map.unloadingPlace">Unloading place</T>
									</p>
								</SypacText>
							</div>
						</div>
					))}
			</MapContainer>
		</div>
	);
};

export default Map;
